import { useState, useEffect, useCallback } from 'react';
import {
  Droplets, Loader2, AlertTriangle, Sparkles, RefreshCw, CheckCircle2, Calculator,
  TrendingUp, TrendingDown, Wallet, Coins, ExternalLink,
} from 'lucide-react';
import {
  getLpStudioChains, getLpStudioPositions, simulateLpRebalance,
} from '../services/api';
import { InfoTip } from '../components/ui';

const DEFAULT_CHAIN = 'all';

function fmtUsd(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
}
function fmtNum(n, d = 4) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
}
function fmtPrice(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v < 0.01) return v.toExponential(3);
  if (v < 1) return v.toFixed(4);
  if (v < 1000) return v.toFixed(2);
  return v.toFixed(0);
}

export default function LpStudioPage() {
  const [chains, setChains] = useState([]);
  const [wallet, setWallet] = useState('');
  const [chain, setChain] = useState(DEFAULT_CHAIN);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getLpStudioChains();
        setChains((res?.data || res)?.chains || []);
      } catch (e) { setError(e?.message || 'Failed to load chains'); }
    })();
  }, []);

  const fetchPositions = useCallback(async () => {
    if (!wallet.trim()) { setError('Enter a wallet address'); return; }
    setLoading(true); setError(null); setSnapshot(null); setSelectedPosition(null);
    try {
      const res = await getLpStudioPositions({ wallet: wallet.trim(), chain });
      const data = res?.data || res;
      // Normalize: the all-chains response wraps per-chain results in `chains:
      // [...]`, the single-chain response is flat. Collapse both into the
      // same shape so the rest of the UI doesn't branch.
      const normalized = data.chains
        ? {
            isAllChains: true,
            positions: data.chains.flatMap(c => (c.positions || []).map(p => ({ ...p, chainKey: p.chainKey || c.chainKey }))),
            totalUsd: data.totalUsd,
            totalUnclaimedFeesUsd: data.totalUnclaimedFeesUsd,
            chainKey: 'all',
            partiallyPriced: data.partiallyPriced,
            truncated: data.truncated,
            chainErrors: data.chainErrors || [],
            perChainCounts: data.chains.map(c => ({ chainKey: c.chainKey, count: c.positions?.length || 0, error: c.error })),
          }
        : { isAllChains: false, ...data, chainErrors: [] };
      setSnapshot(normalized);
    } catch (e) {
      setError(e?.message || 'Failed to fetch positions');
    } finally { setLoading(false); }
  }, [wallet, chain]);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-blue-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-blue-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
            <Droplets className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Uniswap V3 LP Studio</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 font-mono">Elite · Trader's Suite</span>
            </div>
            <p className="text-xs text-blue-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Pulls your concentrated-liquidity positions on-chain. In-range status, unclaimed fees, USD value, plus a capital-efficiency simulator for any proposed new range.
              <InfoTip side="bottom">READ-ONLY — works on any public wallet, no signing. In-range = earning fees, out-of-range = idle. Execute rebalances via Uniswap UI; LP Studio just plans them.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {/* Wallet + chain picker */}
      <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,200px,auto] gap-3 items-end">
          <Field label="Wallet address (any EVM address)">
            <input
              value={wallet}
              onChange={e => setWallet(e.target.value)}
              placeholder="0x..."
              className="ws-input font-mono"
            />
          </Field>
          <Field label="Chain">
            <select value={chain} onChange={e => setChain(e.target.value)} className="ws-input">
              <option value="all">All Chains ({chains.length})</option>
              {chains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <button
            onClick={fetchPositions}
            disabled={loading || !wallet.trim()}
            className="px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/40 text-blue-200 hover:bg-blue-500/30 text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Scan positions
          </button>
        </div>
        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
          <Wallet className="w-3 h-3" />
          Read-only. Your private keys never leave your wallet — we only query the public chain for positions owned by this address.
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {snapshot && (
        <>
          {/* Truncation notice — single-chain only, since the all-chains response
              has its own per-chain truncation surfaced via chainErrors. */}
          {snapshot.truncated && !snapshot.isAllChains && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Showing {snapshot.returnedCount} of {snapshot.totalCount} positions on {chains.find(c => c.id === snapshot.chainKey)?.label || snapshot.chainKey}. Re-query per chain to see the rest.
            </div>
          )}

          {/* Per-chain errors when scanning all chains — usually means a public
              RPC was down/rate-limited; the user can re-query that one chain
              to retry without losing the rest. */}
          {snapshot.chainErrors?.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-xs text-amber-200 space-y-1">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Some chains couldn't be scanned right now ({snapshot.chainErrors.length}/{chains.length}):
              </div>
              <ul className="ml-6 space-y-0.5 text-amber-100/80">
                {snapshot.chainErrors.map(ce => (
                  <li key={ce.chain}>
                    <span className="font-mono">{chains.find(c => c.id === ce.chain)?.label || ce.chain}</span>
                    {ce.error ? <span className="text-gray-400"> — {ce.error}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard
              icon={<Coins className="w-4 h-4 text-blue-300" />}
              label="Positions found"
              value={snapshot.positions?.length || 0}
              sub={
                snapshot.positions?.length === 0
                  ? (snapshot.isAllChains ? 'No V3 positions on any scanned chain' : 'No V3 positions on this chain')
                  : (snapshot.isAllChains
                      ? `Across ${snapshot.perChainCounts?.filter(p => p.count > 0).length || 0} chain${(snapshot.perChainCounts?.filter(p => p.count > 0).length || 0) === 1 ? '' : 's'}`
                      : `On ${chains.find(c => c.id === snapshot.chainKey)?.label || snapshot.chainKey}`)
              }
            />
            <KpiCard
              icon={<Droplets className="w-4 h-4 text-cyan-300" />}
              label="Total position value"
              value={fmtUsd(snapshot.totalUsd)}
              sub={snapshot.partiallyPriced ? 'Sum excludes positions we couldn\'t price' : 'Sum of all positions, USD'}
            />
            <KpiCard
              icon={<Sparkles className="w-4 h-4 text-emerald-300" />}
              label="Unclaimed fees"
              value={fmtUsd(snapshot.totalUnclaimedFeesUsd)}
              sub={snapshot.partiallyPriced ? 'Excludes unpriced positions' : 'Across all positions'}
              tone="positive"
            />
          </div>

          {/* Position list */}
          {snapshot.positions?.length > 0 ? (
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-white">Positions</h2>
              {snapshot.positions.map(p => (
                <PositionCard
                  key={`${p.chainKey}-${p.tokenId}`}
                  position={p}
                  chainLabel={chains.find(c => c.id === p.chainKey)?.label || p.chainKey}
                  showChain={snapshot.isAllChains}
                  selected={selectedPosition?.tokenId === p.tokenId && selectedPosition?.chainKey === p.chainKey}
                  onSelect={() => setSelectedPosition(
                    (selectedPosition?.tokenId === p.tokenId && selectedPosition?.chainKey === p.chainKey) ? null : p
                  )}
                />
              ))}
              {selectedPosition && (
                <RebalanceSimulator position={selectedPosition} />
              )}
            </div>
          ) : (
            <div className="text-center py-10 bg-dark-800/40 border border-dark-700 rounded-2xl">
              <Droplets className="w-10 h-10 text-gray-500 mx-auto mb-2" />
              <div className="text-sm text-gray-400">
                {snapshot.isAllChains
                  ? `No V3 positions found for this wallet on any of the ${chains.length} scanned chains.`
                  : `No V3 positions found for this wallet on ${snapshot.chainKey}.`}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, sub, tone = 'neutral' }) {
  const cls = tone === 'positive' ? 'text-emerald-300' : 'text-white';
  return (
    <div className="bg-dark-800/60 border border-dark-700 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono flex items-center gap-1.5 mb-2">{icon}{label}</div>
      <div className={`text-2xl font-bold ${cls}`}>{value}</div>
      <div className="text-[11px] text-gray-500 mt-1">{sub}</div>
    </div>
  );
}

function PositionCard({ position: p, selected, onSelect, chainLabel, showChain }) {
  return (
    <div className={`bg-dark-800/60 border rounded-2xl transition ${selected ? 'border-blue-500/60' : 'border-dark-700 hover:border-dark-500'}`}>
      <button onClick={onSelect} className="w-full p-4 text-left">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {showChain && (
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-purple-500/15 text-purple-300 border-purple-500/30">{chainLabel}</span>
              )}
              <span className="text-base font-bold text-white font-mono">{p.token0.symbol} / {p.token1.symbol}</span>
              <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-blue-500/15 text-blue-300 border-blue-500/30">{p.feeTierLabel}</span>
              {p.isInRange
                ? <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />in range</span>
                : <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/30 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />out of range</span>}
              {p.priceUnavailable && <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border bg-gray-500/15 text-gray-300 border-gray-500/30">price unavailable</span>}
            </div>
            <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-2 font-mono">
              <span>tokenId #{p.tokenId}</span>
              <a href={`https://app.uniswap.org/pools/${p.tokenId}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-300 flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                view on Uniswap <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>
          </div>
          <div className="text-right">
            {p.usdValue == null ? (
              <>
                <div className="text-lg font-bold text-gray-400" title="USD pricing unavailable for at least one token in this pair">—</div>
                <div className="text-[11px] text-gray-500">USD value unknown</div>
              </>
            ) : (
              <>
                <div className="text-lg font-bold text-white">{fmtUsd(p.usdValue)}</div>
                <div className="text-[11px] text-emerald-300">+{fmtUsd(p.unclaimedFeesUsd)} unclaimed</div>
              </>
            )}
          </div>
        </div>

        {/* Range visualization */}
        <RangeBar
          priceLower={p.priceLower}
          priceUpper={p.priceUpper}
          priceCurrent={p.priceCurrent}
          inRange={p.isInRange}
          symbol0={p.token0.symbol}
          symbol1={p.token1.symbol}
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
          <Cell label={`${p.token0.symbol} amount`} value={fmtNum(p.token0.amount, 6)} />
          <Cell label={`${p.token1.symbol} amount`} value={fmtNum(p.token1.amount, 6)} />
          <Cell label="Tick range" value={`${p.tickLower} → ${p.tickUpper}`} mono />
          <Cell label="Current tick" value={p.tickCurrent} mono />
        </div>
      </button>
    </div>
  );
}

function Cell({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-mono mb-0.5">{label}</div>
      <div className={`text-white ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function RangeBar({ priceLower, priceUpper, priceCurrent, inRange, symbol0, symbol1 }) {
  // Visualize the price range with current price as a marker.
  // We extend the visible window 30% beyond the range on each side so
  // out-of-range positions still show the marker.
  if (priceLower == null || priceUpper == null || priceCurrent == null) {
    return <div className="text-[11px] text-gray-500 py-2">Range display unavailable</div>;
  }
  const pad = (priceUpper - priceLower) * 0.3;
  const viewMin = Math.min(priceLower - pad, priceCurrent - pad);
  const viewMax = Math.max(priceUpper + pad, priceCurrent + pad);
  const span = viewMax - viewMin || 1;
  const rangeStart = ((priceLower - viewMin) / span) * 100;
  const rangeEnd = ((priceUpper - viewMin) / span) * 100;
  const cur = ((priceCurrent - viewMin) / span) * 100;
  const rangeColor = inRange ? 'bg-emerald-500/30 border-emerald-500/50' : 'bg-amber-500/20 border-amber-500/40';
  const markerColor = inRange ? 'bg-emerald-400' : 'bg-amber-400';

  return (
    <div className="space-y-1">
      <div className="relative h-8 bg-dark-900/60 rounded border border-dark-700">
        <div
          className={`absolute top-0 bottom-0 border-2 rounded ${rangeColor}`}
          style={{ left: `${rangeStart}%`, width: `${Math.max(2, rangeEnd - rangeStart)}%` }}
          title={`Range: ${fmtPrice(priceLower)} – ${fmtPrice(priceUpper)}`}
        />
        <div
          className={`absolute top-0 bottom-0 w-0.5 ${markerColor}`}
          style={{ left: `${Math.max(0, Math.min(100, cur))}%` }}
          title={`Current: ${fmtPrice(priceCurrent)}`}
        >
          <div className={`absolute -top-1 -translate-x-1/2 w-2 h-2 rotate-45 ${markerColor}`} />
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-gray-500 font-mono">
        <span>Lower: {fmtPrice(priceLower)}</span>
        <span className={inRange ? 'text-emerald-300' : 'text-amber-300'}>Current: {fmtPrice(priceCurrent)}</span>
        <span>Upper: {fmtPrice(priceUpper)}</span>
      </div>
      <div className="text-[10px] text-gray-500 text-center">{symbol1} per {symbol0}</div>
    </div>
  );
}

function RebalanceSimulator({ position }) {
  const [newLower, setNewLower] = useState(position.priceCurrent != null ? Number((position.priceCurrent * 0.9).toFixed(6)) : 0);
  const [newUpper, setNewUpper] = useState(position.priceCurrent != null ? Number((position.priceCurrent * 1.1).toFixed(6)) : 0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function simulate() {
    setRunning(true); setError(null); setResult(null);
    try {
      const res = await simulateLpRebalance({
        currentPrice: position.priceCurrent,
        oldPriceLower: position.priceLower,
        oldPriceUpper: position.priceUpper,
        newPriceLower: Number(newLower),
        newPriceUpper: Number(newUpper),
      });
      setResult(res?.data || res);
    } catch (e) {
      setError(e?.message || 'Simulation failed');
    } finally { setRunning(false); }
  }

  return (
    <div className="bg-dark-800/60 border border-blue-500/30 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Calculator className="w-4 h-4 text-blue-300" />
        <h3 className="text-sm font-semibold text-white">Rebalance simulator — {position.token0.symbol}/{position.token1.symbol}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="New lower price">
          <input type="number" step="0.000001" value={newLower} onChange={e => setNewLower(e.target.value)} className="ws-input font-mono" />
        </Field>
        <Field label="New upper price">
          <input type="number" step="0.000001" value={newUpper} onChange={e => setNewUpper(e.target.value)} className="ws-input font-mono" />
        </Field>
        <div className="flex items-end">
          <button onClick={simulate} disabled={running} className="px-4 py-2 rounded-lg bg-blue-500/20 border border-blue-500/40 text-blue-200 hover:bg-blue-500/30 text-sm font-medium transition flex items-center gap-2 disabled:opacity-50">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            Simulate
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-gray-500">
        {[0.5, 1, 2, 5, 10, 20].map(w => (
          <button
            key={w}
            onClick={() => {
              setNewLower(Number((position.priceCurrent * (1 - w / 100)).toFixed(6)));
              setNewUpper(Number((position.priceCurrent * (1 + w / 100)).toFixed(6)));
            }}
            className="text-[11px] px-2 py-1.5 rounded border border-dark-700 bg-dark-900/40 hover:border-blue-500/40 text-gray-300 hover:text-blue-200"
          >
            ±{w}%
          </button>
        ))}
      </div>

      {error && <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{error}</div>}

      {result && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RangeMetricBox title="Current range" range={result.oldRange} accent="amber" />
            <RangeMetricBox title="Proposed range" range={result.newRange} accent="emerald" />
          </div>
          <div className={`rounded-xl p-3 border ${result.efficiencyMultiplier >= 1 ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
            <div className="flex items-center gap-2 mb-1">
              {result.efficiencyMultiplier >= 1
                ? <TrendingUp className="w-4 h-4 text-emerald-400" />
                : <TrendingDown className="w-4 h-4 text-amber-400" />}
              <span className="font-semibold text-white text-sm">
                {result.efficiencyMultiplier}× capital efficiency vs current
              </span>
            </div>
            <div className="text-xs text-gray-300">{result.interpretation}</div>
          </div>
          <div className="text-[11px] text-gray-500 space-y-1">
            {result.caveats.map((c, i) => <div key={i}>• {c}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

function RangeMetricBox({ title, range, accent = 'emerald' }) {
  const cls = accent === 'amber' ? 'border-amber-500/30 bg-amber-500/5' : 'border-emerald-500/30 bg-emerald-500/5';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <div className="text-[11px] uppercase tracking-wider text-gray-400 font-mono mb-2">{title}</div>
      <div className="text-xs space-y-1">
        <div className="flex justify-between"><span className="text-gray-400">Lower</span><span className="font-mono text-white">{fmtPrice(range.priceLower)}</span></div>
        <div className="flex justify-between"><span className="text-gray-400">Upper</span><span className="font-mono text-white">{fmtPrice(range.priceUpper)}</span></div>
        <div className="flex justify-between"><span className="text-gray-400">Width</span><span className="font-mono text-white">{range.widthPct}%</span></div>
        <div className="flex justify-between"><span className="text-gray-400">Efficiency</span><span className="font-mono text-white">{range.efficiency}×</span></div>
      </div>
    </div>
  );
}
